import React, { useRef, useCallback } from 'react'

const LEVELS = [
  {
    value: 1,
    label: 'Running on fumes',
    subtitle: 'Nothing left in the tank',
    bars: 1,
    color: '#E24B4A',
    bgTint: '#FCEBEB',
    titleColor: '#501313',
    subtitleColor: '#791F1F',
  },
  {
    value: 2,
    label: 'Low',
    subtitle: 'Pushing through',
    bars: 2,
    color: '#D85A30',
    bgTint: '#FAECE7',
    titleColor: '#4A1B0C',
    subtitleColor: '#712B13',
  },
  {
    value: 3,
    label: 'Steady',
    subtitle: 'Holding a stable pace',
    bars: 3,
    color: '#BA7517',
    bgTint: '#FAEEDA',
    titleColor: '#412402',
    subtitleColor: '#854F0B',
  },
  {
    value: 4,
    label: 'Good',
    subtitle: 'Recharged and productive',
    bars: 4,
    color: '#639922',
    bgTint: '#EAF3DE',
    titleColor: '#173404',
    subtitleColor: '#27500A',
  },
  {
    value: 5,
    label: 'On fire',
    subtitle: 'Fully charged, ready to go',
    bars: 4,
    color: '#1D9E75',
    bgTint: '#E1F5EE',
    titleColor: '#04342C',
    subtitleColor: '#0F6E56',
    lightningBolt: true,
  },
]

export { LEVELS as ENERGY_LEVELS }

function BatteryIcon({ level, isSelected, disabled }) {
  const barXPositions = [6, 14, 22, 30]

  return (
    <svg width="100%" viewBox="0 0 52 42" fill="none" style={{ display: 'block', maxWidth: 52 }}>
      {/* Battery body */}
      <rect
        x="2" y="8" width="42" height="26" rx="4"
        stroke={level.color}
        strokeWidth={isSelected ? 2.5 : 2}
        fill="none"
      />
      {/* Positive terminal */}
      <rect x="44" y="15" width="4" height="12" rx="1" fill={level.color} />
      {/* Internal bars */}
      {barXPositions.slice(0, level.bars).map((x) => (
        <rect key={x} x={x} y="12" width="6" height="18" rx="1" fill={level.color} />
      ))}
      {/* Lightning bolt for level 5 */}
      {level.lightningBolt && (
        <path
          d="M27 2 L19 22 L25 22 L21 40 L33 18 L27 18 Z"
          fill="#F5C21E"
          stroke="#B8860B"
          strokeWidth="0.8"
        />
      )}
    </svg>
  )
}

export function EnergyBatteryScale({ value, onChange, disabled }) {
  const buttonsRef = useRef([])
  const selected = value?.value ?? null
  const selectedLevel = selected ? LEVELS.find((l) => l.value === selected) : null

  const handleClick = useCallback((level) => {
    if (disabled) return
    onChange({ value: level.value, label: level.label })
  }, [disabled, onChange])

  const handleKeyDown = useCallback((e, index) => {
    if (disabled) return
    let nextIndex = null
    if (e.key === 'ArrowRight') {
      nextIndex = Math.min(index + 1, LEVELS.length - 1)
    } else if (e.key === 'ArrowLeft') {
      nextIndex = Math.max(index - 1, 0)
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick(LEVELS[index])
      return
    } else {
      return
    }
    e.preventDefault()
    buttonsRef.current[nextIndex]?.focus()
  }, [disabled, handleClick])

  const focusableIndex = selected ? LEVELS.findIndex((l) => l.value === selected) : 0

  return (
    <div style={styles.container}>
      {/* Battery row */}
      <div style={styles.batteryRow}>
        {LEVELS.map((level, index) => {
          const isSelected = selected === level.value
          const opacity = disabled ? 0.4 : isSelected ? 1 : 0.55
          return (
            <button
              key={level.value}
              ref={(el) => { buttonsRef.current[index] = el }}
              style={{
                ...styles.batteryButton,
                opacity,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'opacity var(--duration-fast) ease',
              }}
              onClick={() => handleClick(level)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onMouseEnter={(e) => {
                if (!disabled && !isSelected) e.currentTarget.style.opacity = '0.8'
              }}
              onMouseLeave={(e) => {
                if (!disabled && !isSelected) e.currentTarget.style.opacity = '0.55'
              }}
              disabled={disabled}
              tabIndex={index === focusableIndex ? 0 : -1}
              aria-label={`Energy level ${level.value}: ${level.label}`}
              aria-pressed={isSelected}
            >
              <BatteryIcon level={level} isSelected={isSelected} disabled={disabled} />
            </button>
          )
        })}
      </div>

      {/* Verdict panel */}
      {selectedLevel && (
        <div
          style={{
            ...styles.verdictPanel,
            background: selectedLevel.bgTint,
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ ...styles.accentBar, background: selectedLevel.color }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: selectedLevel.titleColor }}>
              {selectedLevel.label}
            </div>
            <div style={{ fontSize: 11, color: selectedLevel.subtitleColor }}>
              {selectedLevel.subtitle}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 120,
    padding: '12px 14px',
  },
  batteryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '0 4px',
    overflow: 'hidden',
  },
  batteryButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    flex: '1 1 0',
    minWidth: 0,
  },
  verdictPanel: {
    marginTop: 14,
    padding: '10px 14px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  accentBar: {
    width: 6,
    height: 24,
    borderRadius: 3,
  },
}
